import React, {useEffect, useMemo, useState} from "react";
import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import {
    AlertCircle,
    Award,
    Building,
    Calendar,
    CheckCircle,
    Clock,
    Key,
    Lock,
    Mail,
    Phone,
    Shield,
    Unlock,
    User,
    XCircle
} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {PageHeader} from "../../shared/ui";
import {getProfile} from "./ProfileService";
import {getBackendWarningMessage} from "../../shared/utils";

const formatPhone = (ddi, number) => {
    if (!number) return '-';
    return ddi ? `+${ddi} ${number}` : number;
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatDate = (value) => {
    if (!value) return 'Sem expiração';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
};

const getAccessTypeLabel = (type) => {
    if (type === 'ADMIN') return 'Administrador';
    if (type === 'SUPPORT') return 'Suporte';
    if (type === 'USER') return 'Usuário';
    return type || '-';
};

const getAccessTypeColor = (type) => {
    if (type === 'ADMIN') return 'success';
    if (type === 'SUPPORT') return 'info';
    if (type === 'USER') return 'warning';
    return 'default';
};

const getInitials = (profile) => {
    const source = profile?.name || profile?.username || profile?.email || 'U';
    const chunks = String(source).trim().split(/\s+/).filter(Boolean);
    if (!chunks.length) return 'U';
    if (chunks.length === 1) return chunks[0].slice(0, 2).toUpperCase();
    return `${chunks[0][0] || ''}${chunks[1][0] || ''}`.toUpperCase();
};

const getDisplayName = (profile) => profile?.name || profile?.username || '-';

const ProfileListItem = ({icon, label, value, mono = false}) => (
    <ListItem sx={{px: 0, py: 1.5}}>
        <Box
            sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#ccfbf1',
                color: '#0f766e',
                mr: 2,
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
        <ListItemText
            primary={label}
            secondary={value || '-'}
            primaryTypographyProps={{variant: 'body2', color: 'text.secondary'}}
            secondaryTypographyProps={{
                variant: 'body1',
                fontWeight: 600,
                color: 'text.primary',
                sx: mono ? {fontFamily: 'monospace', wordBreak: 'break-all'} : undefined,
            }}
        />
    </ListItem>
);

const StatusCard = ({icon, label, value, iconColor}) => (
    <Card variant="outlined" sx={{height: '100%', borderRadius: 3}}>
        <CardContent>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{mb: 1.25}}>
                <Box sx={{display: 'grid', placeItems: 'center', color: iconColor}}>
                    {icon}
                </Box>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Stack>
            <Typography variant="h6" fontWeight={700}>{value}</Typography>
        </CardContent>
    </Card>
);

export default function ProfilePage({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const [open, setOpen] = useState(openParent);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [warn, setWarn] = useState('');

    const menus = [
        {label: "Meu perfil", id: "profile", selected: true, disabled: false, icon: <User size={20} />, onClick: () => navigate("/profile")},
    ];

    const handleMenuClose = (isOpen) => setOpen(isOpen);

    useEffect(() => {
        let active = true;

        async function fetchProfile() {
            try {
                setLoading(true);
                const response = await getProfile();
                if (!active) return;
                setProfile(response.data?.valor || response.data);
            } catch (requestError) {
                if (!active) return;
                const backendMessage = getBackendWarningMessage(requestError);
                if (backendMessage) {
                    setWarn(backendMessage);
                } else {
                    setError('Não foi possível carregar os dados do perfil.');
                }
            } finally {
                if (active) setLoading(false);
            }
        }

        fetchProfile();

        return () => {
            active = false;
        };
    }, []);

    const isGlobalUser = useMemo(() => (
        profile?.specialAccessType === 'ADMIN' || profile?.specialAccessType === 'SUPPORT'
    ), [profile?.specialAccessType]);

    const licenses = Array.isArray(profile?.licenses) ? profile.licenses : [];
    const roles = Array.isArray(profile?.roles) ? profile.roles : [];

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <PageHeader
                title="Meu perfil"
                subtitle=""
                titleAction={() => navigate("/")}
                itemsMenu={menus}
                onHandleMenuOpen={() => null}
                onHandleDrawerToggle={handleMenuClose}
                openParent={open}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: {xs: 2.5, md: 4},
                    minWidth: 0,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create(['margin', 'width'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    mt: 8,
                    background: 'linear-gradient(180deg, #f0fdfa 0%, #f8fafc 28%, #f8fafc 100%)',
                    minHeight: 'calc(100dvh - 64px)',
                }}
            >
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h4" component="h1" fontWeight={700} gutterBottom>
                            Meu Perfil
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Visualize suas informações pessoais, status de acesso e vínculos atuais no backoffice.
                        </Typography>
                    </Box>

                    {warn ? <Alert severity="warning" onClose={() => setWarn('')}>{warn}</Alert> : null}
                    {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}

                    {loading ? (
                        <Paper sx={{p: 4, display: 'grid', placeItems: 'center', minHeight: 260, borderRadius: 4}}>
                            <CircularProgress />
                        </Paper>
                    ) : (
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={8}>
                                <Stack spacing={3}>
                                    <Paper variant="outlined" sx={{p: {xs: 2.5, md: 3}, borderRadius: 4}}>
                                        <Stack direction={{xs: 'column', sm: 'row'}} spacing={3} alignItems={{sm: 'center'}}>
                                            <Avatar
                                                sx={{
                                                    width: 88,
                                                    height: 88,
                                                    bgcolor: '#0f766e',
                                                    fontSize: '2rem',
                                                    fontWeight: 700,
                                                    boxShadow: '0 18px 35px rgba(15, 118, 110, 0.18)',
                                                }}
                                            >
                                                {getInitials(profile)}
                                            </Avatar>
                                            <Box sx={{flex: 1}}>
                                                <Typography variant="h5" fontWeight={700}>
                                                    {getDisplayName(profile)}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5, mb: 1.5}}>
                                                    @{profile?.username || '-'}
                                                </Typography>
                                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                    <Chip
                                                        label={getAccessTypeLabel(profile?.specialAccessType)}
                                                        color={getAccessTypeColor(profile?.specialAccessType)}
                                                        size="small"
                                                        icon={<Shield size={16} />}
                                                    />
                                                    <Chip
                                                        label={profile?.enabled ? 'Conta ativa' : 'Conta inativa'}
                                                        color={profile?.enabled ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                    {profile?.blocked ? <Chip label="Bloqueada" color="error" size="small" /> : null}
                                                    {profile?.mustRenewPassword ? <Chip label="Renovar senha" color="warning" size="small" /> : null}
                                                </Stack>
                                            </Box>
                                        </Stack>

                                        <Divider sx={{my: 3}} />

                                        <Typography variant="h6" fontWeight={700} sx={{mb: 1}}>
                                            Informações Pessoais
                                        </Typography>

                                        <List disablePadding>
                                            <ProfileListItem icon={<User size={20} />} label="Nome de usuário" value={profile?.username || '-'} />
                                            <Divider component="li" />
                                            <ProfileListItem icon={<Mail size={20} />} label="E-mail" value={profile?.email || '-'} />
                                            <Divider component="li" />
                                            <ProfileListItem icon={<Phone size={20} />} label="Telefone" value={formatPhone(profile?.ddiPhoneNumber, profile?.mobilePhoneNumber)} />
                                            <Divider component="li" />
                                            <ProfileListItem icon={<Shield size={20} />} label="ID do usuário" value={profile?.userId || '-'} mono />
                                        </List>
                                    </Paper>

                                    <Paper variant="outlined" sx={{p: {xs: 2.5, md: 3}, borderRadius: 4}}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Informações de Acesso
                                        </Typography>

                                        <Alert
                                            severity={isGlobalUser ? 'success' : 'info'}
                                            icon={<Shield size={18} />}
                                            sx={{mt: 2, mb: 3, borderRadius: 3}}
                                        >
                                            {isGlobalUser
                                                ? profile?.specialAccessType === 'ADMIN'
                                                    ? 'Acesso global de administrador: sua conta possui alcance completo no sistema.'
                                                    : 'Acesso global de suporte: sua conta possui visão ampla para atendimento e operação.'
                                                : 'Acesso por escopo: sua conta depende das licenças e roles vinculadas.'}
                                        </Alert>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <StatusCard
                                                    icon={profile?.enabled ? <CheckCircle size={20} /> : <XCircle size={20} />}
                                                    label="Status da conta"
                                                    value={profile?.enabled ? 'Ativa' : 'Inativa'}
                                                    iconColor={profile?.enabled ? '#16a34a' : '#dc2626'}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <StatusCard
                                                    icon={profile?.blocked ? <Lock size={20} /> : <Unlock size={20} />}
                                                    label="Bloqueio"
                                                    value={profile?.blocked ? 'Bloqueada' : 'Desbloqueada'}
                                                    iconColor={profile?.blocked ? '#dc2626' : '#16a34a'}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <StatusCard
                                                    icon={<Key size={20} />}
                                                    label="Renovação de senha"
                                                    value={profile?.mustRenewPassword ? 'Pendente' : 'Não requerida'}
                                                    iconColor={profile?.mustRenewPassword ? '#f59e0b' : '#16a34a'}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <StatusCard
                                                    icon={<Calendar size={20} />}
                                                    label="Expiração"
                                                    value={formatDate(profile?.expirationDate)}
                                                    iconColor="#0f766e"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>

                                    {!isGlobalUser ? (
                                        <Paper variant="outlined" sx={{p: {xs: 2.5, md: 3}, borderRadius: 4}}>
                                            <Typography variant="h6" fontWeight={700} gutterBottom>
                                                Licenças e Permissões
                                            </Typography>

                                            <Box sx={{mt: 3}}>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
                                                    <Building size={18} color="#0f766e" />
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        Licenças vinculadas
                                                    </Typography>
                                                </Stack>
                                                {licenses.length ? (
                                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                        {licenses.map((license) => (
                                                            <Chip
                                                                key={license?.id}
                                                                label={`${license?.customer?.legalName || license?.empresa?.razao_social || 'Licença'} - ${license?.plan?.productPlanName || license?.plano?.nome_plano || license?.status || '-'}`}
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Nenhuma licença vinculada.
                                                    </Typography>
                                                )}
                                            </Box>

                                            <Divider sx={{my: 3}} />

                                            <Box>
                                                <Stack direction="row" spacing={1} alignItems="center" sx={{mb: 2}}>
                                                    <Award size={18} color="#0f766e" />
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        Roles vinculadas
                                                    </Typography>
                                                </Stack>
                                                {roles.length ? (
                                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                        {roles.map((role) => (
                                                            <Chip
                                                                key={role?.roleId || role?.id}
                                                                label={role?.roleName || role?.nome || '-'}
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Stack>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Nenhuma role vinculada.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Paper>
                                    ) : null}
                                </Stack>
                            </Grid>

                            <Grid item xs={12} lg={4}>
                                <Stack spacing={3}>
                                    <Paper variant="outlined" sx={{p: {xs: 2.5, md: 3}, borderRadius: 4}}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Atividade
                                        </Typography>

                                        <List disablePadding sx={{pt: 1}}>
                                            <ProfileListItem icon={<Clock size={20} />} label="Último acesso" value={formatDateTime(profile?.lastLogin)} />
                                            <Divider component="li" />
                                            <ProfileListItem icon={<Calendar size={20} />} label="Conta criada em" value={formatDateTime(profile?.createdAt)} />
                                            <Divider component="li" />
                                            <ProfileListItem icon={<Calendar size={20} />} label="Última atualização" value={formatDateTime(profile?.updatedAt)} />
                                        </List>
                                    </Paper>

                                    <Paper variant="outlined" sx={{p: {xs: 2.5, md: 3}, borderRadius: 4}}>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            Resumo de Segurança
                                        </Typography>

                                        <Stack spacing={2} sx={{mt: 2}}>
                                            <Box sx={{p: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0'}}>
                                                <Stack direction="row" spacing={1.25} alignItems="center" sx={{mb: 1}}>
                                                    <AlertCircle size={18} color={profile?.mustRenewPassword ? '#f59e0b' : '#16a34a'} />
                                                    <Typography fontWeight={700}>Política de senha</Typography>
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary">
                                                    {profile?.mustRenewPassword
                                                        ? 'Sua conta exige renovação de senha no próximo acesso.'
                                                        : 'Não há exigência imediata de renovação de senha.'}
                                                </Typography>
                                            </Box>

                                            <Box sx={{p: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0'}}>
                                                <Stack direction="row" spacing={1.25} alignItems="center" sx={{mb: 1}}>
                                                    <Shield size={18} color="#0f766e" />
                                                    <Typography fontWeight={700}>Escopo atual</Typography>
                                                </Stack>
                                                <Typography variant="body2" color="text.secondary">
                                                    {isGlobalUser
                                                        ? 'Seu escopo de atuação é global dentro do backoffice.'
                                                        : `Seu acesso está vinculado a ${licenses.length} licença(s) e ${roles.length} role(s).`}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Stack>
                            </Grid>
                        </Grid>
                    )}
                </Stack>
            </Box>
        </Box>
    );
}
